import type { TFunction } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  type CurrentUser,
  openingHours as loadOpeningHours,
  type OpeningHour,
  type ShiftSlot,
  saveOpeningHours,
  shiftCalendar,
} from '../api.ts';
import { InfoTip } from '../components/InfoTip.tsx';
import { useAnnounce } from '../hooks/useAnnounce.tsx';
import { apiMessage } from '../messages.ts';
import { formatTime } from './ShiftsPage.tsx';

/**
 * "Orari e turni", la schermata del coordinamento.
 *
 * Gli orari decidono quando il widget è aperto, e lo stato "chiuso" è la
 * schermata più vista del prodotto (sezione 11): è la modifica con l'effetto
 * più grande che un ente possa fare da solo. Per questo qui si vede, accanto
 * agli orari, **chi copre i prossimi giorni** — accorciare le fasce senza
 * sapere dove sono i buchi è il modo di crearne altri. Il mock 3 le mette
 * affiancate, ed è la relazione che conta: accorcio il mercoledì, vedo chi
 * perde il turno.
 *
 * Due conseguenze che nessuno si aspetta, dette esplicitamente: cambiare gli
 * orari non interrompe nessuna conversazione in corso, e i turni futuri che
 * escono dalle nuove fasce vengono accorciati o tolti. Dalla fase 4 chi li
 * aveva prenotati **riceve una notifica**, e chi non ce l'ha attiva resta da
 * avvisare a voce: il messaggio dopo il salvataggio dice quante persone sono
 * nell'uno e nell'altro caso, perché è la differenza fra un avviso e
 * l'illusione di un avviso.
 */

/** Lunedì per primo: è come si legge una settimana di turni. */
const WEEK = [1, 2, 3, 4, 5, 6, 0];

type Props = { user: CurrentUser; headingRef?: React.RefObject<HTMLHeadingElement | null> };

/** Una fascia in modifica, con un'identità stabile che l'indice non dà. */
type Draft = { id: string; hour: OpeningHour };

let nextDraftId = 0;

function toDraft(hours: readonly OpeningHour[]): Draft[] {
  return sortHours(hours).map((hour) => {
    nextDraftId += 1;
    return { id: `w${nextDraftId}`, hour };
  });
}

export function HoursPage({ user, headingRef }: Props) {
  const { t, i18n } = useTranslation();

  const [draft, setDraft] = useState<Draft[]>();
  const [editable, setEditable] = useState(false);
  const [timezone, setTimezone] = useState<string>();
  const [slots, setSlots] = useState<ShiftSlot[]>();
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const { say, announcer } = useAnnounce();

  const load = useCallback(async () => {
    try {
      const [hours, calendar] = await Promise.all([loadOpeningHours(), shiftCalendar()]);
      setDraft(toDraft(hours.hours));
      setEditable(hours.editable);
      setTimezone(hours.timezone);
      setSlots(calendar.slots ?? []);
      setFailed(false);
    } catch {
      // Senza uno stato d'errore la schermata mostrava sette giorni "Chiuso" e
      // "solo chi coordina può cambiare gli orari" a una coordinatrice: due
      // affermazioni false nella stessa schermata.
      setFailed(true);
      say(t('hours.load_failed'), 'danger');
    }
  }, [say, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const locale = i18n.resolvedLanguage;
  const zone = timezone ?? user.tenant?.timezone;

  /** Dove portare il focus dopo aver aggiunto o tolto una fascia. */
  const formRef = useRef<HTMLUListElement>(null);
  const focusAfter = useRef<{ id: string; field: 'opens' | 'add' } | undefined>(undefined);

  useEffect(() => {
    // Come sopra: si aspetta il ridisegno della settimana, non il momento in
    // cui la fascia è stata aggiunta allo stato.
    const target = draft && focusAfter.current;
    if (!target) return;
    focusAfter.current = undefined;
    const selector = target.field === 'opens' ? `#opens-${target.id}` : `[data-add="${target.id}"]`;
    formRef.current?.querySelector<HTMLElement>(selector)?.focus();
  }, [draft]);

  const save = async (): Promise<void> => {
    if (!draft || saving) return;
    setSaving(true);
    try {
      const result = await saveOpeningHours(draft.map((entry) => entry.hour));
      setDraft(toDraft(result.hours));
      say(savedMessage(result.shifts, t));
      // La copertura cambia con gli orari: si rilegge, non si indovina.
      await load();
    } catch (error) {
      say(
        error instanceof ApiError
          ? apiMessage(t, error.messageKey, {
              ...error.details,
              // Il server dice **quale giorno**: senza tradurlo il messaggio
              // mostrerebbe un numero, e con quattordici fasce nessuno sa
              // dove guardare.
              ...(typeof error.details?.weekday === 'number'
                ? { day: t(`weekday.${error.details.weekday}`) }
                : {}),
            })
          : t('hours.save_failed'),
        'danger',
      );
    } finally {
      setSaving(false);
    }
  };

  const update = (id: string, patch: Partial<OpeningHour>): void => {
    setDraft((current) =>
      current?.map((entry) =>
        entry.id === id ? { ...entry, hour: { ...entry.hour, ...patch } } : entry,
      ),
    );
  };

  const addWindow = (weekday: number): void => {
    // La fascia nuova copia l'ultima già scritta: nessun orario inventato nel
    // codice, e nove volte su dieci è quella giusta da correggere appena.
    const template =
      draft?.findLast((entry) => entry.hour.weekday === weekday)?.hour ?? draft?.[0]?.hour;
    nextDraftId += 1;
    const id = `w${nextDraftId}`;
    setDraft((current) =>
      sortDrafts([
        ...(current ?? []),
        {
          id,
          hour: {
            weekday,
            opensAt: template?.opensAt ?? '',
            closesAt: template?.closesAt ?? '',
          },
        },
      ]),
    );
    focusAfter.current = { id, field: 'opens' };
    say(t('hours.window_added', { day: t(`weekday.${weekday}`) }));
  };

  const removeWindow = (id: string, weekday: number): void => {
    setDraft((current) => current?.filter((entry) => entry.id !== id));
    focusAfter.current = { id: String(weekday), field: 'add' };
    say(t('hours.window_removed', { day: t(`weekday.${weekday}`) }));
  };

  const coverage = slots ?? [];
  /**
   * La conseguenza di quello che si sta facendo, **dove** lo si sta facendo.
   *
   * Sotto i 60rem la colonna "Copertura" scende in fondo alla pagina: chi
   * accorcia il mercoledì da un telefono non vede mai chi perde il turno, cioè
   * proprio la relazione per cui il mock 3 mette le due cose affiancate. Il
   * numero delle fasce scoperte costa una riga e sta accanto agli orari a
   * qualunque larghezza; l'elenco per fascia resta dov'è.
   */
  const gaps = coverage.filter((slot) => slot.covered === 0).length;
  const windows = draft?.length ?? 0;

  return (
    <main className="page" id="main" tabIndex={-1}>
      <h2 ref={headingRef} tabIndex={-1}>
        {t('hours.title')}
      </h2>

      {announcer}
      {failed ? (
        <p>
          <button type="button" className="button" onClick={() => void load()}>
            {t('shifts.retry')}
          </button>
        </p>
      ) : null}

      <div className="hours-layout">
        <section className="card" aria-labelledby="hours-heading">
          {/*
            Non "Orari e turni" un'altra volta: è il titolo della pagina. Accanto
            al titolo, quante fasce ha davanti chi guarda: da lì escono i
            ventinove controlli sotto, e finora non lo diceva niente.
          */}
          <div className="section-head">
            <div className="section-head__title">
              <h3 id="hours-heading">{t('hours.week_title')}</h3>
              <InfoTip>{t('hours.description')}</InfoTip>
            </div>
            {draft === undefined ? null : (
              <p className="count">{t('hours.windows_count', { count: windows })}</p>
            )}
          </div>

          {draft === undefined ? (
            /*
              Tre righe grigie, non la parola "Chiuso" sette volte: finché gli
              orari non sono arrivati la schermata non sa se l'ente è aperto, e
              non deve dirlo. Chi ascolta la pagina riceve la frase, non le
              barre (`.skeleton` è decorativa).
            */
            <>
              <p className="visually-hidden">{t('hours.loading')}</p>
              <span className="skeleton skeleton--wide" aria-hidden="true" />
              <span className="skeleton skeleton--wide" aria-hidden="true" />
              <span className="skeleton skeleton--half" aria-hidden="true" />
            </>
          ) : (
            <>
              <p className="muted">
                {t('hours.timezone', {
                  now: formatTime(new Date().toISOString(), locale, zone),
                })}
              </p>
              {editable ? null : <p className="note">{t('hours.read_only')}</p>}
              {draft.length === 0 ? (
                <p className="banner banner--warn">{t('hours.no_windows')}</p>
              ) : null}
              {/*
                Quante fasce restano scoperte, detto qui **solo dove serve**.

                La scheda "Copertura" lo dice già con la sua pastiglia, e da
                60rem in su sta a fianco e resta appiccicata mentre si scorre:
                lì questo banner era lo stesso numero detto due volte a mezzo
                schermo di distanza, in due forme e due colori — "Una fascia
                scoperta in questo periodo" contro "1 scoperta".

                Sotto i 60rem però la copertura scivola **in fondo alla
                pagina**, e chi accorcia il mercoledì da un telefono non
                vedrebbe mai chi perde il turno. Quindi la riga resta nel
                documento e la nasconde il foglio di stile alla larghezza in cui
                l'altra è già in vista (`.hours__gaps`): a quel punto non è
                un'informazione tolta, è la stessa informazione detta una volta
                sola.
              */}
              {slots === undefined ? null : (
                <p className={gaps > 0 ? 'banner banner--warn hours__gaps' : 'note hours__gaps'}>
                  {gaps > 0
                    ? t('shifts.uncovered_count', { count: gaps })
                    : t('shifts.uncovered_none')}
                </p>
              )}

              <ul className="hours" ref={formRef}>
                {WEEK.map((weekday) => {
                  const dayWindows = draft.filter((entry) => entry.hour.weekday === weekday);
                  const day = t(`weekday.${weekday}`);

                  return (
                    <li className="hours__day" key={weekday}>
                      {/*
                        Il giorno e il suo comando sulla stessa riga.

                        "Aggiungi una fascia" stava in fondo al blocco, dopo le
                        fasce, con lo stesso aspetto del "Togli" di ognuna:
                        appartiene al **giorno**, e adesso sta dove il giorno si
                        nomina. Il titolo, che era l'unica cosa a strutturare
                        ventinove controlli, ha smesso di essere più piccolo del
                        testo dei paragrafi (`styles.css`).
                      */}
                      <div className="section-head">
                        <h4>{day}</h4>
                        {editable ? (
                          <button
                            type="button"
                            className="button"
                            data-add={weekday}
                            aria-label={t('hours.add_named', { day })}
                            onClick={() => addWindow(weekday)}
                          >
                            {t('hours.add')}
                          </button>
                        ) : null}
                      </div>

                      {dayWindows.length === 0 ? (
                        <p className="muted">{t('hours.closed_day')}</p>
                      ) : (
                        dayWindows.map(({ id, hour }, index) => (
                          <fieldset className="hours__window" key={id}>
                            {/*
                              A schermo il riquadro dice già che i tre controlli
                              stanno insieme; a chi ascolta la pagina lo dice la
                              legenda, che porta il giorno e il numero della
                              fascia — con due fasce nello stesso giorno,
                              "Togli" da solo non dice quale.
                            */}
                            <legend className="visually-hidden">
                              {t('hours.window_legend', { day, index: index + 1 })}
                            </legend>
                            <div className="field field--inline">
                              <label htmlFor={`opens-${id}`}>{t('hours.opens_label')}</label>
                              <input
                                id={`opens-${id}`}
                                type="time"
                                value={hour.opensAt}
                                disabled={!editable}
                                /*
                                  Il nome accessibile porta il giorno: in un
                                  elenco di campi modulo, sei voci "Apre" tutte
                                  uguali non dicono a un coordinatore cieco
                                  quale sia il lunedì e quale il giovedì.
                                */
                                aria-label={t('hours.opens_named', { day })}
                                onChange={(event) => update(id, { opensAt: event.target.value })}
                              />
                            </div>
                            <div className="field field--inline">
                              <label htmlFor={`closes-${id}`}>{t('hours.closes_label')}</label>
                              <input
                                id={`closes-${id}`}
                                type="time"
                                value={hour.closesAt === '24:00' ? '' : hour.closesAt}
                                disabled={!editable}
                                aria-label={t('hours.closes_named', { day })}
                                /*
                                  Tutte le chiusure a mezzanotte rimandano alla
                                  **stessa** riga: la spiegazione era ripetuta
                                  sotto ogni fascia, centotrentaquattro
                                  caratteri per volta, e con sette giorni
                                  aperti fino a mezzanotte era la metà del
                                  testo della schermata. `aria-describedby` può
                                  puntare allo stesso identificatore da più
                                  campi, quindi chi ascolta la sente comunque
                                  sul campo che sta compilando.
                                */
                                aria-describedby={
                                  hour.closesAt === '24:00' ? 'hours-midnight' : undefined
                                }
                                onChange={(event) => update(id, { closesAt: event.target.value })}
                              />
                            </div>
                            {editable ? (
                              <button
                                type="button"
                                /* Ciò che toglie lo dice: era identico ad
                                   "Aggiungi", accanto ad "Aggiungi". */
                                className="button button--danger"
                                aria-label={t('hours.remove_named', {
                                  day,
                                  from: hour.opensAt,
                                  to: hour.closesAt,
                                })}
                                onClick={() => removeWindow(id, weekday)}
                              >
                                {t('hours.remove')}
                              </button>
                            ) : null}
                          </fieldset>
                        ))
                      )}
                    </li>
                  );
                })}
              </ul>

              {/* Il campo non sa mostrare le 24:00 e resterebbe vuoto senza
                  spiegazione, mentre il valore salvato esiste
                  (`docs/DECISIONI.md`, S10). Una volta sola, per tutte. */}
              {draft.some((entry) => entry.hour.closesAt === '24:00') ? (
                <p className="muted" id="hours-midnight">
                  {t('hours.midnight_hint')}
                </p>
              ) : null}
              <p className="muted">{t('hours.overnight_hint')}</p>

              {editable ? (
                <>
                  {/*
                    Cambia etichetta, non si disabilita: un pulsante che si
                    disabilita mentre lavora perde il fuoco, e chi salva da
                    tastiera si ritrova a inizio pagina proprio mentre aspetta
                    di sapere quanti turni sono cambiati (U2). Il doppio tocco
                    lo ferma la guardia dentro `save`.
                  */}
                  {/* La conseguenza che nessuno si aspetta, detta **prima** del
                      pulsante e non sotto: sotto la leggeva chi aveva già
                      salvato. */}
                  <p className="note">{t('hours.active_sessions_note')}</p>
                  <button
                    type="button"
                    className="button button--primary button--wide"
                    onClick={() => void save()}
                  >
                    {t(saving ? 'hours.saving' : 'hours.save')}
                  </button>
                </>
              ) : null}
            </>
          )}
        </section>

        <section className="card" aria-labelledby="coverage-heading">
          <div className="section-head">
            <div className="section-head__title">
              <h3 id="coverage-heading">{t('hours.coverage_title')}</h3>
              <InfoTip>{t('hours.coverage_hint')}</InfoTip>
            </div>
            {slots === undefined || coverage.length === 0 ? null : (
              <p className="count">{t('shifts.gaps_count', { count: gaps })}</p>
            )}
          </div>
          {slots === undefined ? (
            /* Non "non c'è niente da coprire" finché non si sa se c'è. */
            <>
              <p className="visually-hidden">{t('shifts.loading')}</p>
              <span className="skeleton skeleton--wide" aria-hidden="true" />
              <span className="skeleton skeleton--wide" aria-hidden="true" />
              <span className="skeleton skeleton--half" aria-hidden="true" />
            </>
          ) : coverage.length === 0 ? (
            <p className="empty">{t('hours.coverage_empty')}</p>
          ) : (
            <ul className="calendar__slots">
              {coverage.map((slot) => (
                <li
                  key={slot.startsAt}
                  className={slot.covered === 0 ? 'coverage is-gap' : 'coverage'}
                >
                  <span>
                    {t('shifts.slot_when', {
                      day: formatCoverageDay(slot.startsAt, locale, zone),
                      from: formatTime(slot.startsAt, locale, zone),
                      to: formatTime(slot.endsAt, locale, zone),
                    })}
                  </span>
                  <span className="coverage__status">
                    <span>
                      {slot.covered === 0
                        ? t('hours.coverage_none')
                        : t('hours.coverage_people', { count: slot.covered })}
                    </span>
                    {/*
                      Coperta a metà non è coperta: senza questa riga, un
                      volontario che entra alle 20:30 su una fascia
                      19:00–23:00 faceva risultare l'intera fascia coperta, e
                      le ore scoperte sparivano proprio dalla vista di chi
                      cerca i buchi — lo stesso difetto già corretto in
                      `ShiftsPage.tsx` (`slot.fullyCovered`, calcolato dal
                      server in `staff-shifts.ts`), mai arrivato qui.
                    */}
                    {slot.covered > 0 && !slot.fullyCovered ? (
                      <span className="calendar__partial">{t('shifts.covered_partly')}</span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}

/** Ordinamento stabile: la settimana come si legge, e le fasce in orario. */
function sortHours(hours: readonly OpeningHour[]): OpeningHour[] {
  return [...hours].sort((a, b) => a.weekday - b.weekday || a.opensAt.localeCompare(b.opensAt));
}

function sortDrafts(entries: readonly Draft[]): Draft[] {
  return [...entries].sort(
    (a, b) => a.hour.weekday - b.hour.weekday || a.hour.opensAt.localeCompare(b.hour.opensAt),
  );
}

function formatCoverageDay(
  iso: string,
  locale: string | undefined,
  timeZone: string | undefined,
): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      weekday: 'short',
      day: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

/**
 * Cosa è successo davvero salvando gli orari.
 *
 * Tre informazioni, in ordine di conseguenza: quanti turni sono stati
 * accorciati o tolti, a quante persone è arrivata la notifica, e **quante non
 * l'hanno ricevuta**. L'ultima è quella che conta per chi coordina: fino alla
 * fase 3 questa frase diceva "diglielo tu" a tutti, perché il canale non
 * esisteva; adesso resta da dirlo solo a chi le notifiche non le ha.
 */
function savedMessage(
  shifts: { adjusted: number; removed: number; notified: number; unreachable: number },
  t: TFunction,
): string {
  if (shifts.adjusted + shifts.removed === 0) return t('hours.saved');

  const parts = [
    t('hours.saved_with_shifts', { adjusted: shifts.adjusted, removed: shifts.removed }),
  ];
  if (shifts.notified > 0) parts.push(t('hours.saved_notified', { count: shifts.notified }));
  if (shifts.unreachable > 0) {
    parts.push(t('hours.saved_unreachable', { count: shifts.unreachable }));
  }
  return parts.join(' ');
}
